import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuctionModal from './AuctionModal';
import NotificationBell from './NotificationBell';
import { FaGavel, FaHistory } from 'react-icons/fa';
import logoWhite from '../logo.png';
import useNotifications from '../hooks/useNotifications';
import axios from '../services/api';
import log from '../utils/logger';

const Navbar = ({ socket }) => {
  const { notifications, setNotifications, clearNotifications, updateNotifications } = useNotifications(socket);
  const { loginWithRedirect, logout, isAuthenticated, user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const dropdownRef = useRef(null);

  const handleLogin = () => {
    loginWithRedirect();
  };

  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      }
    }
  }, [isAuthenticated, navigate]);

  // Load profile picture from localStorage and backend
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Immediately load from localStorage for fast display
    const cached = localStorage.getItem('profilePicture');
    if (cached) {
      setProfilePicture(cached);
    }
    
    // Then fetch from backend for the latest value
    const fetchProfilePicture = async () => {
      try {
        const response = await axios.get('/profile');
        if (response.data.success && response.data.user.profilePicture) {
          setProfilePicture(response.data.user.profilePicture);
          localStorage.setItem('profilePicture', response.data.user.profilePicture);
        }
      } catch {
        // Fallback to Auth0 picture if profile fetch fails
      }
    };
    fetchProfilePicture();

    // Listen for profile picture updates from Settings page
    const handleStorageChange = (e) => {
      if (e.key === 'profilePicture' && e.newValue) {
        setProfilePicture(e.newValue);
      }
    };
    const handleProfilePictureUpdate = (e) => {
      if (e.detail?.profilePicture) {
        setProfilePicture(e.detail.profilePicture);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    };
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout({ returnTo: window.location.origin });
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    const handleEscape = (e) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <nav className="bg-dark-header border-b border-dark-border shadow-lg sticky top-0 z-50" aria-label="Main navigation">
      <div className="px-4">
        <div className="flex items-center justify-between h-16">
          {/* Left Side - Logo and Navigation */}
          <div className="flex items-center space-x-4">
            <div
              className="flex items-center cursor-pointer group"
              onClick={() => navigate('/')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); } }}
              role="link"
              tabIndex={0}
              aria-label="SnipeHouse home"
            >
              <img 
                src={logoWhite} 
                alt="SnipeHouse" 
                className="h-9 sm:h-10 drop-shadow-lg group-hover:scale-105 transition-all duration-300"
              />
            </div>
            
            <div className="hidden md:flex items-center">
              <button
                onClick={() => navigate('/historical')}
                className={`flex items-center font-medium transition-all duration-200 px-3 py-2 relative ${
                  location.pathname === '/historical' 
                    ? 'text-white' 
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <FaHistory className="mr-2 w-4 h-4" aria-hidden="true" />
                Historical Auctions
                {location.pathname === '/historical' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-snipehouse-blue"></div>
                )}
              </button>
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-2">
            {/* List Item Button - Hidden on mobile (lg:flex) */}
            {isAuthenticated && (
              <button
                onClick={async () => {
                  try {
                    const response = await axios.get('/profile');
                    if (!response.data.user.stripeConnectAccountId) {
                      navigate('/settings?stripe_required=true');
                    } else {
                      setModalOpen(true);
                    }
                  } catch (error) {
                    log.error('Error checking user:', error);
                    setModalOpen(false);
                  }
                }}
                className="hidden lg:flex items-center bg-snipehouse-blue hover:bg-snipehouse-blue-dark hover:scale-105 text-white px-3 sm:px-4 py-2.5 rounded-lg transition-all duration-300 font-medium shadow-glow-blue-soft hover:shadow-glow-blue whitespace-nowrap"
              >
                <FaGavel className="sm:mr-2 w-4 h-4 animate-bounce-slow" aria-hidden="true" />
                <span className="hidden sm:inline">List Item</span>
              </button>
            )}

            {/* Notification Bell - Hidden on mobile (lg:block) */}
            {isAuthenticated && (
              <div className="hidden lg:block">
                <NotificationBell 
                  notifications={notifications}
                  user={user}
                  setNotifications={setNotifications}
                  onClearNotifications={clearNotifications}
                  updateNotifications={updateNotifications}
                />
              </div>
            )}

            {/* User Menu */}
            <div className="relative" ref={dropdownRef}>
              {isAuthenticated ? (
                <>
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center space-x-2 p-1 rounded-lg hover:bg-white/10 transition-all duration-200 group backdrop-blur-sm"
                    aria-haspopup="true"
                    aria-expanded={dropdownOpen}
                    aria-label="User menu"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden text-white font-semibold text-sm shadow-lg">
                      {profilePicture || user.picture ? (
                        <img src={profilePicture || user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-fun flex items-center justify-center animate-gradient bg-[length:200%_200%]">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="hidden md:block text-gray-200 font-medium group-hover:text-white">
                      {user.name?.split(' ')[0]}
                    </span>
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-white transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-dark-card rounded-lg shadow-xl border border-dark-border py-1 z-50" role="menu" aria-label="User menu">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-dark-border">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden text-white font-semibold shadow-lg">
                            {profilePicture || user.picture ? (
                              <img src={profilePicture || user.picture} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-fun flex items-center justify-center animate-gradient bg-[length:200%_200%]">
                                {user.name?.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white truncate">{user.name}</div>
                            <div className="text-sm text-gray-400 truncate">{user.email}</div>
                          </div>
                        </div>
                      </div>

                      {/* Mobile Navigation Items */}
                      <div className="md:hidden border-b border-dark-border py-1">
                        <button
                          onClick={() => {
                            navigate('/historical');
                            setDropdownOpen(false);
                          }}
                          role="menuitem"
                          className="flex items-center w-full px-4 py-2 text-gray-300 hover:bg-white/5 hover:text-white transition-colors duration-150"
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Historical Auctions
                        </button>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate('/orders');
                            setDropdownOpen(false);
                          }}
                          role="menuitem"
                          className="flex items-center w-full px-4 py-2 text-gray-300 hover:bg-white/5 hover:text-white transition-colors duration-150"
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          My Orders
                        </button>
                        <button
                          onClick={() => {
                            navigate('/my-auctions');
                            setDropdownOpen(false);
                          }}
                          role="menuitem"
                          className="flex items-center w-full px-4 py-2 text-gray-300 hover:bg-white/5 hover:text-white transition-colors duration-150"
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          My Auctions
                        </button>
                        <button
                          onClick={() => {
                            navigate('/settings');
                            setDropdownOpen(false);
                          }}
                          role="menuitem"
                          className="flex items-center w-full px-4 py-2 text-gray-300 hover:bg-white/5 hover:text-white transition-colors duration-150"
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Settings
                        </button>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-dark-border pt-1">
                        <button
                          onClick={handleLogout}
                          role="menuitem"
                          className="flex items-center w-full px-4 py-2 text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors duration-150"
                        >
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={handleLogin}
                  className="bg-snipehouse-blue hover:bg-snipehouse-blue-dark hover:scale-105 text-white px-4 py-2 rounded-lg transition-all duration-300 font-medium shadow-glow-blue-soft hover:shadow-glow-blue"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <AuctionModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </nav>
  );
};

Navbar.propTypes = {
  socket: PropTypes.object,
};

export default Navbar;